import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from './supabase';

// ============================================================================
// Auth context
// ----------------------------------------------------------------------------
// Wraps Supabase Auth session. MFA verification is SERVER-ENFORCED: the
// mfaVerified flag is derived from the session_has_mfa() RPC (backed by
// saas.mfa_verified_sessions, keyed to the GoTrue session with a TTL), NOT from
// localStorage. localStorage is only an optimistic hint to avoid a flash while
// the server check runs, and is cleared whenever the server says "no". The data
// layer additionally fails closed through RLS regardless of this flag.
// ============================================================================

interface AuthContextValue {
    session: Session | null;
    user: User | null;
    loading: boolean;
    mfaVerified: boolean | null; // null = still checking the server
    refreshMfa: () => Promise<void>;
    signOut: () => Promise<void>;
}

const MFA_HINT_KEY = 'pt-or:mfa-verified';
const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [session, setSession] = useState<Session | null>(null);
    const [loading, setLoading] = useState(true);
    const [mfaVerified, setMfaVerified] = useState<boolean | null>(
        localStorage.getItem(MFA_HINT_KEY) === 'true' ? true : null
    );

    const checkMfa = useCallback(async (activeSession: Session | null) => {
        if (!activeSession) { setMfaVerified(false); return; }
        const { data, error } = await supabase.rpc('session_has_mfa');
        const verified = !error && data === true;
        setMfaVerified(verified);
        if (verified) localStorage.setItem(MFA_HINT_KEY, 'true');
        else localStorage.removeItem(MFA_HINT_KEY);
    }, []);

    const refreshMfa = useCallback(async () => {
        const { data: { session: s } } = await supabase.auth.getSession();
        await checkMfa(s);
    }, [checkMfa]);

    useEffect(() => {
        supabase.auth.getSession().then(({ data: { session } }) => {
            setSession(session);
            setLoading(false);
            void checkMfa(session);
        });

        const { data: { subscription } } = supabase.auth.onAuthStateChange(
            (_event, nextSession) => {
                setSession(nextSession);
                if (!nextSession) {
                    localStorage.removeItem(MFA_HINT_KEY);
                    sessionStorage.removeItem('pt-or:enrollment-in-progress');
                    setMfaVerified(false);
                } else {
                    void checkMfa(nextSession);
                }
            }
        );

        return () => subscription.unsubscribe();
    }, [checkMfa]);

    const signOut = async () => {
        localStorage.removeItem(MFA_HINT_KEY);
        sessionStorage.removeItem('pt-or:enrollment-in-progress');
        sessionStorage.removeItem('pt-or:encounter-context');
        setMfaVerified(false);
        await supabase.auth.signOut();
    };

    return (
        <AuthContext.Provider
            value={{ session, user: session?.user ?? null, loading, mfaVerified, refreshMfa, signOut }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
}
